import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  CircleDashed,
  CircleDot,
  Sparkles,
} from "lucide-react";
import {
  lmsApi,
  TraineeLearningPathData,
  TraineeLearningPathScheduleRange,
} from "../api";
import { useAuth } from "../hooks/useAuth";

type StepStatus = "completed" | "in_progress" | "upcoming" | "not_started" | "unknown";

type StepDefinition = {
  key: string;
  label: string;
  progressKeys?: string[];
  scheduleKey: keyof TraineeLearningPathData["schedule"];
  kind: "sessions" | "assessment";
};

const STEP_DEFINITIONS: StepDefinition[] = [
  {
    key: "launchpad",
    label: "Launchpad",
    scheduleKey: "launchpad",
    kind: "sessions",
  },
  {
    key: "pre_assessment",
    label: "Pre Assessment",
    progressKeys: ["Pre Assessment", "Pre-Assessment"],
    scheduleKey: "pre_assessment",
    kind: "assessment",
  },
  {
    key: "basecamp",
    label: "Base Camp",
    progressKeys: ["Base Camp", "Basecamp"],
    scheduleKey: "basecamp",
    kind: "sessions",
  },
  {
    key: "domain",
    label: "Domain Training",
    progressKeys: ["Domain Training"],
    scheduleKey: "domain",
    kind: "sessions",
  },
  {
    key: "ojt",
    label: "OJT",
    progressKeys: ["OJT"],
    scheduleKey: "ojt",
    kind: "sessions",
  },
  {
    key: "post_assessment",
    label: "Post Assessment",
    progressKeys: ["Post Assessment", "Post-Assessment"],
    scheduleKey: "post_assessment",
    kind: "assessment",
  },
];

const STATUS_LABELS: Record<StepStatus, string> = {
  completed: "Complete",
  in_progress: "In progress",
  upcoming: "Upcoming",
  not_started: "Not started",
  unknown: "Unavailable",
};

const STATUS_STYLES: Record<
  StepStatus,
  {
    badge: string;
    dot: string;
    ring: string;
    line: string;
    bar: string;
    icon: typeof Circle;
  }
> = {
  completed: {
    badge: "bg-emerald-100 text-emerald-700",
    dot: "bg-emerald-500",
    ring: "ring-emerald-200",
    line: "bg-emerald-200",
    bar: "bg-emerald-500",
    icon: CheckCircle2,
  },
  in_progress: {
    badge: "bg-blue-100 text-blue-700",
    dot: "bg-blue-500",
    ring: "ring-blue-200",
    line: "bg-blue-200",
    bar: "bg-blue-500",
    icon: CircleDot,
  },
  upcoming: {
    badge: "bg-amber-100 text-amber-700",
    dot: "bg-amber-500",
    ring: "ring-amber-200",
    line: "bg-amber-200",
    bar: "bg-amber-500",
    icon: CircleDashed,
  },
  not_started: {
    badge: "bg-gray-100 text-gray-600",
    dot: "bg-gray-300",
    ring: "ring-gray-200",
    line: "bg-gray-200",
    bar: "bg-gray-300",
    icon: Circle,
  },
  unknown: {
    badge: "bg-gray-100 text-gray-600",
    dot: "bg-gray-300",
    ring: "ring-gray-200",
    line: "bg-gray-200",
    bar: "bg-gray-300",
    icon: Circle,
  },
};

const parseDate = (value?: string | null) => {
  if (!value) return null;
  const normalized = value.length === 10 ? `${value}T00:00:00` : value;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value?: string | null) => {
  const parsed = parseDate(value ?? null);
  if (!parsed) return "TBD";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsed);
};

const formatRange = (start?: string | null, end?: string | null) => {
  if (!start && !end) return "Dates pending";
  if (start && end) {
    const startLabel = formatDate(start);
    const endLabel = formatDate(end);
    return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
  }
  return formatDate(start ?? end ?? null);
};

const pickProgressValue = (progress: Record<string, any> | null, keys: string[]) => {
  if (!progress) return null;
  for (const key of keys) {
    if (key in progress) return progress[key];
  }
  return null;
};

const parseProgressPercent = (value: any) => {
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const match = value.match(/(\d+(?:\.\d+)?)\s*%/);
    if (match) return Number(match[1]);
  }
  return null;
};

const normalizeProgressText = (value: any) => {
  if (value == null) return "";
  return String(value).trim().toLowerCase();
};

const getSessionStats = (range?: TraineeLearningPathScheduleRange | null) => {
  if (!range?.dates?.length) return null;
  const today = new Date();
  const todayFloor = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const completed = range.dates.filter((date) => {
    const parsed = parseDate(date);
    return parsed ? parsed <= todayFloor : false;
  }).length;
  const total = range.dates.length;
  const percent = total ? Math.round((completed / total) * 100) : null;
  return { completed, total, percent };
};

const deriveStatus = (
  progressValue: any,
  range: { start?: string | null; end?: string | null },
  percent: number | null
): StepStatus => {
  if (percent != null) {
    if (percent >= 100) return "completed";
    if (percent > 0) return "in_progress";
  }

  const text = normalizeProgressText(progressValue);
  if (text.includes("completed")) return "completed";
  if (text.includes("progress") || text.includes("started")) return "in_progress";
  if (text.includes("not") || text.includes("pending")) return "not_started";

  const startDate = parseDate(range.start ?? null);
  const endDate = parseDate(range.end ?? null);
  const now = new Date();

  if (startDate && now < startDate) return "upcoming";
  if (endDate && now > endDate) return "completed";
  if (startDate && endDate && now >= startDate && now <= endDate) return "in_progress";

  return progressValue == null ? "unknown" : "not_started";
};

const TraineeLearningPath: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TraineeLearningPathData | null>(null);
  const [expandedStep, setExpandedStep] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const fetchProgress = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await lmsApi.getTraineeProgress();
        if (mounted) {
          if (!response.success || !response.data) {
            throw new Error(response.message || "Unable to load learning path.");
          }
          setData(response.data);
        }
      } catch (err: any) {
        if (mounted) {
          setError(err?.message || "Unable to load learning path.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    fetchProgress();
    return () => {
      mounted = false;
    };
  }, []);

  const steps = useMemo(() => {
    if (!data) return [];
    return STEP_DEFINITIONS.map((def) => {
      const progressValue = def.progressKeys
        ? pickProgressValue(data.progress, def.progressKeys)
        : null;
      const percentFromProgress = parseProgressPercent(progressValue);

      const scheduleValue = data.schedule[def.scheduleKey];
      const range: TraineeLearningPathScheduleRange =
        def.kind === "sessions"
          ? (scheduleValue as TraineeLearningPathScheduleRange)
          : {
              dates: [],
              start: (scheduleValue as { start: string | null }).start ?? null,
              end: (scheduleValue as { end: string | null }).end ?? null,
            };

      const sessionStats = def.kind === "sessions" ? getSessionStats(range) : null;
      const percent = percentFromProgress ?? sessionStats?.percent ?? null;
      const status = deriveStatus(progressValue, range, percent);

      return {
        key: def.key,
        label: def.label,
        kind: def.kind,
        status,
        percent,
        progressValue,
        range,
        sessionStats,
      };
    });
  }, [data]);

  const nextStep = useMemo(() => {
    return steps.find((step) => step.status !== "completed") ?? null;
  }, [steps]);

  useEffect(() => {
    if (!expandedStep && nextStep?.key) {
      setExpandedStep(nextStep.key);
    }
  }, [expandedStep, nextStep?.key]);

  const overallProgress = useMemo(() => {
    if (!steps.length) return 0;
    const total = steps.reduce((sum, step) => {
      const percent = step.percent ?? (step.status === "completed" ? 100 : 0);
      return sum + percent;
    }, 0);
    return Math.round(total / steps.length);
  }, [steps]);

  const knowlensWarning =
    data?.knowlens?.error ||
    (data?.knowlens?.status != null &&
      data?.knowlens?.status !== 1 &&
      data?.knowlens?.status !== "1")
      ? data?.knowlens?.error || data?.knowlens?.message || "Progress data is unavailable."
      : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      </div>
    );
  }

  if (user?.role !== "TRAINEE") {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600">
          This learning path is only available for trainees.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-500 via-teal-500 to-sky-500 text-white shadow-lg">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.25),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.2),transparent_35%)]" />
        <div className="relative p-8 sm:p-10 space-y-3">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-white/15 p-2">
              <Sparkles className="h-5 w-5" />
            </div>
            <p className="uppercase tracking-[0.15em] text-xs font-semibold text-white/80">
              Learning Path
            </p>
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold">
            Your training trail in one view
          </h1>
          <p className="text-sm sm:text-base text-white/90 max-w-2xl">
            Track every stage of your journey, see upcoming dates, and keep an eye on
            your Knowlens progress as it updates.
          </p>
          <div className="flex flex-wrap gap-4 text-sm text-white/90">
            <span className="inline-flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
              <CalendarDays className="h-4 w-4" /> Dates synced to your batch
            </span>
            <span className="inline-flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
              <CheckCircle2 className="h-4 w-4" /> Progress from Knowlens LMS
            </span>
          </div>
        </div>
      </div>

      {knowlensWarning ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex gap-3">
          <AlertTriangle className="h-5 w-5 mt-0.5" />
          <div>
            <p className="font-semibold">Progress update temporarily unavailable</p>
            <p className="text-xs sm:text-sm text-amber-800/80">{knowlensWarning}</p>
          </div>
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
        <div className="space-y-5">
          {steps.map((step, index) => {
            const styles = STATUS_STYLES[step.status];
            const Icon = styles.icon;
            const isExpanded = expandedStep === step.key;
            const rangeLabel = formatRange(step.range.start ?? null, step.range.end ?? null);

            return (
              <div key={step.key} className="relative flex gap-4">
                <div className="flex flex-col items-center">
                  <div
                    className={`flex h-11 w-11 items-center justify-center rounded-full ${styles.dot} ${styles.ring} ring-4 text-white shadow-sm`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  {index < steps.length - 1 ? (
                    <div className={`w-1 flex-1 ${styles.line} rounded-full mt-2`} />
                  ) : null}
                </div>

                <div className="flex-1 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">{step.label}</h3>
                      <p className="text-sm text-gray-600">{rangeLabel}</p>
                    </div>
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${styles.badge}`}>
                      {STATUS_LABELS[step.status]}
                    </span>
                  </div>

                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Progress</span>
                      <span>
                        {step.percent != null
                          ? `${Math.min(100, Math.max(0, step.percent))}%`
                          : step.progressValue
                            ? String(step.progressValue)
                            : "Not started"}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-100">
                      <div
                        className={`h-2 rounded-full ${styles.bar}`}
                        style={{
                          width: `${step.percent != null ? Math.min(100, Math.max(0, step.percent)) : 0}%`,
                        }}
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setExpandedStep(isExpanded ? null : step.key)}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700"
                  >
                    {isExpanded ? "Hide details" : "Show details"}
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>

                  {isExpanded ? (
                    <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 p-4 text-sm text-gray-600 space-y-2">
                      {step.kind === "sessions" && step.sessionStats ? (
                        <>
                          <div className="flex items-center justify-between">
                            <span>Sessions completed</span>
                            <span className="font-semibold text-gray-900">
                              {step.sessionStats.completed}/{step.sessionStats.total}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(step.range.dates ?? []).map((date) => (
                              <span
                                key={`${step.key}-${date}`}
                                className="rounded-full bg-white px-3 py-1 text-xs text-gray-600 border border-gray-200"
                              >
                                {formatDate(date)}
                              </span>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span>Assessment window</span>
                          <span className="font-semibold text-gray-900">{rangeLabel}</span>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Overall progress</h3>
            <div className="flex items-center justify-between text-2xl font-semibold text-gray-900">
              {overallProgress}%
              <span className="text-xs font-medium text-gray-500">
                {steps.filter((step) => step.status === "completed").length}/{steps.length} steps
              </span>
            </div>
            <div className="h-2 rounded-full bg-gray-100">
              <div
                className="h-2 rounded-full bg-emerald-500"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-2">
            <h3 className="text-sm font-semibold text-gray-900">Current batch</h3>
            <p className="text-lg font-semibold text-gray-900">
              {data?.batch.batch_code}
            </p>
            <p className="text-sm text-gray-600">
              {data?.batch.program_name || "Program details pending"}
            </p>
            <span className="inline-flex w-fit items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
              {data?.batch.status}
            </span>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-2">
            <h3 className="text-sm font-semibold text-gray-900">Next milestone</h3>
            {nextStep ? (
              <>
                <p className="text-base font-semibold text-gray-900">{nextStep.label}</p>
                <p className="text-sm text-gray-600">
                  {formatRange(nextStep.range.start ?? null, nextStep.range.end ?? null)}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-600">All steps completed.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TraineeLearningPath;
